import { DateTime } from 'luxon';
import { ValueResponse, ValueType } from '../../app/types';

export interface GetSeriesResponse {
  startTime: string;
  name: string;
  isTarget: boolean;
  values: Array<ValueResponse>;
  valueType: ValueType;
  elapsedSeconds: Array<number>;
  targetIndex: Array<number>;
};

export interface GetEntriesResponse {
  series: Array<GetSeriesResponse>;
};

const seriesSortOrder = (s: GetSeriesResponse) => s.isTarget ? -1 : 1;

const formatSeriesForPlot = (series: GetSeriesResponse): Plotly.Data => {
  return {
    x: formatElapsedTimes(series.startTime, series.elapsedSeconds),
    y: formatValuesForPlot(series.values),
    type: 'scatter',
    mode: 'lines',
    name: series.name,
    line: formatLineForPlot(series.isTarget),
    xaxis: 'x',
  };
}

export const formatEntriesForPlot = (entries: GetEntriesResponse): Array<Plotly.Data> => {
  const series = entries && entries.series ? [...entries.series] : [];
  series.sort((a, b) => seriesSortOrder(a) - seriesSortOrder(b));
  return series.map(s => formatSeriesForPlot(s));
}

const formatLineForPlot = (isTarget: boolean): Partial<Plotly.ScatterLine> => {
  if (isTarget) {
    return {
      color: '#cccccc', // TODO: Centralized colors.
      dash: 'dashdot',
      width: 3,
    }
  } else {
    return {
      color: '#65AFFF', // TODO: Centralized colors.
      width: 3,
    }
  }
};

const formatValuesForPlot = (values: Array<ValueResponse>): Array<Plotly.Datum> => {
  return values.map(v => {
    switch (typeof v) {
      case 'boolean':
        return v ? 1 : 0
      default:
        return v;
    }
  });
};

export const formatElapsedTimes = (startTime: string, elapsedSeconds: Array<number>): Array<Date> => {
  var timeMarker = DateTime.fromISO(startTime);
  return elapsedSeconds.map(e => {
    timeMarker = timeMarker.plus({ seconds: e });
    return timeMarker.toJSDate();
  });
};

export const renderDifference = (difference: null | number): string => {
  if (difference === null) {
    return "";
  }
  if (difference === 0.0) {
    return "Even";
  }
  if (difference > 0.0) {
    return `${difference.toFixed(2)} above`;
  }
  if (difference < 0.0) {
    return `${Math.abs(difference).toFixed(2)} below`;
  }
  return "";
}

export interface FormattedTableEntry {
  key: string;
  date: Date,
  value: ValueResponse;
  targetValue: ValueResponse;
  difference: string;
};

export const formatEntriesForTable = (entries: GetEntriesResponse): Array<FormattedTableEntry> => {
  const series = entries?.series.find(x => !x.isTarget);
  if (!series) {
    return ([]);
  }
  const targets = entries?.series.find(x => x.isTarget);
  const times = formatElapsedTimes(series.startTime, series.elapsedSeconds);
  const rows = series.values.map((v, i) => {
    const targetIndex = series.targetIndex[i];
    let targetValue = null;
    let difference = null;
    if (targets && targetIndex !== null && targetIndex !== -1) {
      targetValue = targets.values[targetIndex];
      if (typeof targetValue === "number" && typeof v === "number") {
        difference = (v - targetValue);
      }
    }
    return {
      key: i.toString(),
      date: times[i],
      value: v,
      targetValue: targetValue,
      difference: renderDifference(difference),
    };
  }).reverse();
  return rows;
};
